// Choice chips — structured asks. Pure helpers shared by the chat state
// (use-chat), the BFF history mapping (messages API), and the chips UI
// (MessageRow/ChoiceChips). The wire shape is the harness contract: an
// ASSISTANT row carries `metadata.choices`; the answering USER turn carries
// `metadata.choiceAnswer` referencing the ask + tapped option.

export type ChoiceOption = { id: string; label: string };
export type MessageChoices = { askId: string; options: ChoiceOption[] };
export type ChoiceAnswer = { askId: string; optionId: string };

/** Read + sanitize `metadata.choices` off a raw message entity: a valid askId and at least one well-formed {id, label} option, else undefined. */
export function readMessageChoices(metadata: unknown): MessageChoices | undefined {
  const raw = (metadata as { choices?: unknown } | undefined)?.choices;
  if (!raw || typeof raw !== 'object') return undefined;
  const { askId, options } = raw as { askId?: unknown; options?: unknown };
  if (typeof askId !== 'string' || !askId || !Array.isArray(options)) return undefined;
  const clean: ChoiceOption[] = [];
  for (const o of options) {
    const { id, label } = (o ?? {}) as { id?: unknown; label?: unknown };
    if (typeof id === 'string' && id && typeof label === 'string' && label) clean.push({ id, label });
  }
  return clean.length > 0 ? { askId, options: clean } : undefined;
}

/** Read + sanitize `metadata.choiceAnswer` off a raw message entity. */
export function readChoiceAnswer(metadata: unknown): ChoiceAnswer | undefined {
  const raw = (metadata as { choiceAnswer?: unknown } | undefined)?.choiceAnswer;
  if (!raw || typeof raw !== 'object') return undefined;
  const { askId, optionId } = raw as { askId?: unknown; optionId?: unknown };
  if (typeof askId !== 'string' || !askId || typeof optionId !== 'string' || !optionId) return undefined;
  return { askId, optionId };
}

type ChoiceRow = { role?: string; choiceAnswer?: ChoiceAnswer };

/**
 * Selection derives from the transcript: an option is selected when any USER
 * row carries a `choiceAnswer` matching this ask. Covers the optimistic bubble
 * (stamped locally on send) and persisted history alike; multiple answers to
 * the same ask (chips stay tappable) all read as selected.
 */
export function selectedOptionIds(messages: readonly ChoiceRow[], askId: string): Set<string> {
  const ids = new Set<string>();
  for (const m of messages) {
    if (m.role === 'USER' && m.choiceAnswer?.askId === askId) ids.add(m.choiceAnswer.optionId);
  }
  return ids;
}

type Choosable = { id?: string; finalId?: string; choices?: MessageChoices };

/** Patch the matching row's choices in place (matched by id OR persisted finalId); no-op when the row is gone. */
export function patchChoicesById<M extends Choosable>(
  messages: M[],
  messageId: string,
  choices: MessageChoices,
): M[] {
  const matches = (m: M) => m.id === messageId || m.finalId === messageId;
  if (!messages.some(matches)) return messages;
  return messages.map((m) => (matches(m) ? { ...m, choices } : m));
}
